import { FormEvent, useState } from 'react'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { RoutingPaths } from 'app/routes/routes'

import { CustomerForm } from 'entities/excursions/content-page/ui/customer-form'
import { IExcursionsOrder } from 'entities/excursions/models/order-excursions/model'
import { orderExcursionsStore } from 'entities/excursions/models/order-excursions/order-excursions-store'
import { mplusStore } from 'entities/mplus/module/mplus-store'
import userStore from 'entities/user/module/user-store'

import { BuyTicketApi } from 'shared/api/BuyTicket'
import { OrderExcursions } from 'shared/api/excursions/order'
import { BoughtExcursion } from 'shared/api/excursions/order/model'
import { CreditInfo } from 'shared/api/mplus/credit-info'
import { REGEX_EMAIL } from 'shared/constants/regex'
import { usePrivacyPolicyDocument } from 'shared/hooks'
import ErrorImage from 'shared/images/error.png'
import { openDeepLink } from 'shared/lib/deep-link-open'
import { getErrorMessage } from 'shared/lib/errorText'
import { startPaymentFromConfirmResult } from 'shared/lib/payment-start'
import { preparePhoneNumberForRequest } from 'shared/lib/prepare-phone-number'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { IPhoneInput } from 'shared/ui/phone-input'
import { PopupContent } from 'shared/ui/poup-content'

import { prepareCustomerData } from './lib/prepareCustomerForm'

export interface ICustomerDetails {
  first_name: string
  last_name: string
  phone: IPhoneInput
  email: string
}

const CustomerDetails = () => {
  const { t } = useTranslation()
  const { openPrivacyPolicy } = usePrivacyPolicyDocument()

  const navigate = useNavigate()

  const { account } = userStore

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<ICustomerDetails>({
    defaultValues: prepareCustomerData(account),
  })

  const [loading, setLoading] = useState<boolean>(false)

  const { closeDialog, openDialog } = useDialogBottomSheet()

  const onChangeInputEmail = (event: FormEvent<HTMLInputElement>) =>
    event?.currentTarget?.value?.replace(REGEX_EMAIL, '')

  const onShowConform = () => {
    void openPrivacyPolicy('base_public_offer')
  }

  const { people_quantity, total_price, excursion_id } =
    orderExcursionsStore.orderExcursionInfo

  const onSubmitBooked = async (data: ICustomerDetails) => {
    const dataPurchase: IExcursionsOrder = {
      people_quantity,
      total_price,
      client_first_name: data.first_name,
      client_last_name: data.last_name,
      client_email: data?.email,
      client_phone: preparePhoneNumberForRequest(data?.phone) || '',
      excursion_id,
    }

    return OrderExcursions.orderExcursion(dataPurchase)
  }

  const confirmGetTicket = async (response: BoughtExcursion) => {
    try {
      const { data } = await BuyTicketApi.buyTicketConfirm({
        id: response.id,
        type: 'excursion_orders',
      })
      await startPaymentFromConfirmResult(data)
    } catch (err) {
      const errorText = getErrorMessage(err)

      onShowError(errorText)
    } finally {
      setLoading(false)
    }
  }

  const onShowError = (errorMessage?: string) => {
    openDialog({
      child: (
        <PopupContent
          image={ErrorImage}
          title={t('error')}
          description={errorMessage || t('please_tey_again')}
          button={t('try_again')}
          onClick={closeDialog}
        />
      ),
      isDialog: false,
    })
  }

  const purchaseRegular = handleSubmit(async (data) => {
    try {
      setLoading(true)

      const { data: boughExcursion } = await onSubmitBooked(data)

      await confirmGetTicket(boughExcursion?.data)
    } catch (err) {
      setLoading(false)

      const errorText = getErrorMessage(err)

      onShowError(errorText)
    }
  })

  const purchaseMplus = (day: number) => {
    handleSubmit(async (data) => {
      try {
        setLoading(true)

        const { data: boughExcursion } = await onSubmitBooked(data)

        const { data: response } = await CreditInfo.initPayMplus({
          payment_day: day,
          service_type: 'excursion_orders',
          service_id: boughExcursion?.data?.id,
        })

        if (response?.data?.redirect_url) {
          openDeepLink(response?.data?.redirect_url)
        }
      } catch (err) {
        setLoading(false)

        const errorText = getErrorMessage(err)

        onShowError(errorText)
      }
    })()
  }

  const onOpenMplusPage = handleSubmit(async (data) => {
    mplusStore.setExcursionsData(data)

    navigate(RoutingPaths.excursion.mplus.path())
  })

  return (
    <>
      {loading ? null : (
        <CustomerForm
          errors={errors}
          control={control}
          onShowConform={onShowConform}
          onPurchaseMplus={purchaseMplus}
          onOpenMplusPage={onOpenMplusPage}
          onPurchaseRegular={purchaseRegular}
          onChangeInputEmail={onChangeInputEmail}
        />
      )}
    </>
  )
}

export default CustomerDetails
